// React Query hooks for the catalogue (products / work-sources /
// payment-modes) settings pages.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createCatalogueItem,
  deleteCatalogueItem,
  listCatalogue,
  updateCatalogueItem,
  type CatalogueKind,
} from '../../../../api/catalogues';
import { queryKeys } from '../../../../api/queryKeys';
import { useToast } from '../../../../hooks/useToast';
import type {
  CreateReferenceItemInput,
  ReferenceItem,
  UpdateReferenceItemInput,
} from '../../../../types/api';

const KEY_FOR_KIND = {
  products: queryKeys.products,
  workSources: queryKeys.workSources,
  paymentModes: queryKeys.paymentModes,
} as const;

const TOAST_LABEL: Record<CatalogueKind, string> = {
  products: 'product',
  workSources: 'work source',
  paymentModes: 'payment mode',
};

export function useCatalogue(kind: CatalogueKind) {
  return useQuery<ReferenceItem[]>({
    queryKey: KEY_FOR_KIND[kind].list,
    queryFn: () => listCatalogue(kind),
    staleTime: 60_000,
  });
}

export function useCreateCatalogueItem(kind: CatalogueKind) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: CreateReferenceItemInput) =>
      createCatalogueItem(kind, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_FOR_KIND[kind].all });
      toast.success(`${TOAST_LABEL[kind]} added.`);
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error && err.message
          ? err.message
          : `Failed to add ${TOAST_LABEL[kind]}.`;
      toast.error(message);
    },
  });
}

export function useUpdateCatalogueItem(kind: CatalogueKind) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: number;
      input: UpdateReferenceItemInput;
    }) => updateCatalogueItem(kind, id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_FOR_KIND[kind].all });
      toast.success(`${TOAST_LABEL[kind]} updated.`);
    },
    onError: () => {
      toast.error(`Failed to update ${TOAST_LABEL[kind]}.`);
    },
  });
}

export function useDeleteCatalogueItem(kind: CatalogueKind) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (id: number) => deleteCatalogueItem(kind, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_FOR_KIND[kind].all });
      toast.success(`${TOAST_LABEL[kind]} deleted.`);
    },
    onError: () => {
      toast.error(`Failed to delete ${TOAST_LABEL[kind]}.`);
    },
  });
}
